import { Button, InputNumber } from 'antd';
import { useEffect, useRef, useState, type ReactNode } from 'react';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [C6a §2d] THE ONE DELEGATION EDITOR — stacked rows of (target, number), extracted from
 *  `DelegateInline` so a second host gets the SAME interaction rather than a second copy of it.
 *
 *  Three hosts in this tree: the force split on the fill screen (`DelegateInline`, byte-identical to
 *  what it rendered before), the builder's "Ask a division" on a command role (`CommandSection`),
 *  and a division handing command positions to its own units on the fill screen
 *  (`CommandFamilies`' hand-down, [C6a §4]). What they share is the INTERACTION —
 *  [UX1.1 §4]'s one row per target, [C3c §5b]'s Confirm/Cancel on the FIRST row, left-aligned,
 *  "add another" beneath the stack, a seed that a background revalidation cannot snap back, and a
 *  failed read that says so instead of spinning. What they do NOT share is any rule about the
 *  numbers, and that is the whole shape of the props:
 *
 *  ⚠️ THE EDITOR DECIDES NOTHING ABOUT A NUMBER. When a row may be added and when Confirm is
 *  enabled are the HOST's predicates over the rows (`canAddRow`, `canConfirm`); the per-row
 *  floor and the upper bound are the host's too. The force host still states its ceiling, pool
 *  and floor sentences through the caption slots; a COMMAND host passes none, because a command
 *  position is never a number on any screen that describes force (FR-5.2.6b) — the captions are
 *  ABSENT there, not the same sentences with other figures in them.
 *
 *  ⚠️ THE TARGET IS OPAQUE. It is a string the host renders a picker for (`renderTarget`) and
 *  reads back on Confirm: a unit id for the force split, `division|unit` for the builder's ask.
 *  `disabledKeys` is the set chosen on OTHER rows, handed to that picker so a host whose write
 *  refuses a repeat can refuse it where it is typed ([C1b §3]'s unique-unit rule).
 * ─────────────────────────────────────────────────────────────────────────────────
 */

/** One row as the host reads it back: a chosen target (or none yet) and its number. */
export type DelegationRow = { target: string | null; count: number };

/**
 * ⚠️ [C6a review] A ROW'S IDENTITY, MINTED WHEN THE ROW IS — never its target. The React key was
 * `${index}-${target}`, so choosing a target CHANGED the key and React remounted the whole row: the
 * Select that had focus was destroyed mid-choice. On the force host that cost one lost focus; the
 * builder's command host puts TWO pickers in a row (division, then unit), so a keyboard user lost
 * focus on every division choice before reaching the unit. Held in the editor only — every host
 * callback still receives plain `{target, count}` rows (`plainOf`), so no host and no request body
 * ever sees it. A module counter rather than a ref, because it is read while a row is CREATED —
 * including in `useState`'s initialiser, where a ref read is a render-time read.
 */
type EditorRow = DelegationRow & { id: number };
let lastRowId = 0;
const mintRowId = (): number => {
  lastRowId += 1;
  return lastRowId;
};
const plainOf = (rows: readonly EditorRow[]): DelegationRow[] => rows.map(({ target, count }) => ({ target, count }));

export type DelegationTargetProps = {
  index: number;
  value: string | null;
  /** Targets already chosen on OTHER rows. */
  disabledKeys: ReadonlySet<string>;
  onChange: (next: string | null) => void;
};

export type DelegationRowsEditorProps = {
  /**
   * ⚠️ `error` BEFORE `loading`, or a refusal renders as a spinner for ever — `DelegateModal`
   * never consulted it, and opening one on a row the server 400s left it on "Loading…".
   */
  error?: string | null;
  loading?: boolean;
  loadingText?: string;
  /**
   * ⚠️ WHAT THE HOST ALREADY HOLDS, as `{target, count}` — never a `useState` initialiser. The host's
   * read resolves AFTER this mounts, so an initialiser would capture nothing and never correct
   * itself. `undefined` while that read is in flight; `[]` opens on one blank row.
   */
  seed: readonly { target: string; count: number }[] | undefined;
  /** Nothing to pick from: the host's sentence beside a Cancel, and no form. */
  empty?: ReactNode;
  renderTarget: (props: DelegationTargetProps) => ReactNode;
  /** The row's own floor — the force split's `namedFloor`, `0` where there is none. */
  minFor?: (target: string | null) => number;
  max: number;
  canAddRow: (rows: readonly DelegationRow[]) => boolean;
  canConfirm: (rows: readonly DelegationRow[]) => boolean;
  /** A throw is shown as the refusal, in the server's own words ([C1b]: never a code). */
  onConfirm: (rows: readonly DelegationRow[]) => Promise<void> | void;
  onCancel: () => void;
  /** `delegate` for the force split — `delegate-inline`, `delegate-count-0`, `delegate-confirm`… */
  testIdPrefix: string;
  /** `data-delegate-row` for the force split; a command host takes `data-command-delegate-row`. */
  rowAttribute: `data-${string}`;
  copy: {
    confirm: string;
    cancel: string;
    addRow: string;
    refused: string;
    countLabel: (row: DelegationRow, index: number) => string;
  };
  /** Beneath a row's number — the force split's "2 named · cannot go below 2". */
  rowCaption?: (row: DelegationRow, index: number) => ReactNode;
  /** Beneath the stack and the add-another — the force split's reach and ceiling sentences. */
  footer?: ReactNode;
};

export const DelegationRowsEditor = ({
  error,
  loading = false,
  loadingText = 'Loading…',
  seed,
  empty,
  renderTarget,
  minFor = () => 0,
  max,
  canAddRow,
  canConfirm,
  onConfirm,
  onCancel,
  testIdPrefix,
  rowAttribute,
  copy,
  rowCaption,
  footer,
}: DelegationRowsEditorProps) => {
  const blank = (): EditorRow => ({ target: null, count: minFor(null), id: mintRowId() });
  const [rows, setRows] = useState<EditorRow[]>(() => [blank()]);
  const [saving, setSaving] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);

  /*
   * ─────────────────────────────────────────────────────────────────────────────────
   *  ⚠️ [UX1 §2] THE SEED, LATCHED ON A FINGERPRINT OF WHAT IT IS — NEVER A BARE EFFECT.
   *
   *  A bare effect on the host's data is [P3b.3]'s defect verbatim: a background revalidation
   *  lands mid-typing and snaps the operator's numbers back to the server's. Keyed on what the
   *  seed actually IS, an identical revalidation re-seeds nothing, while a genuine change — the
   *  answer after a successful write — does. `ArrangementBuilder`'s `seededFor` is the precedent.
   *
   *  ⚠️ THE FINGERPRINT IS THE SEED, SERIALISED, AND THE EFFECT READS ONLY THE FINGERPRINT. A host
   *  builds its seed array afresh on every render, so depending on the array would run the effect
   *  every render; depending on its serialised form runs it exactly when the content moves.
   * ─────────────────────────────────────────────────────────────────────────────────
   */
  const seededFor = useRef<string | null>(null);
  const fingerprint = seed === undefined ? null : JSON.stringify(seed.map((row) => [row.target, row.count]));
  const floorOfBlank = minFor(null);

  useEffect(() => {
    if (fingerprint === null) return;
    if (seededFor.current === fingerprint) return;
    seededFor.current = fingerprint;
    const seeded = (JSON.parse(fingerprint) as [string, number][]).map(([target, count]) => ({
      target,
      count,
      id: mintRowId(),
    }));
    setRows(seeded.length > 0 ? seeded : [{ target: null, count: floorOfBlank, id: mintRowId() }]);
  }, [fingerprint, floorOfBlank]);

  const prefix = testIdPrefix;
  const rowHandle = (index: number): Record<string, string> => ({ [rowAttribute]: String(index) });

  if (error) {
    return (
      <p className="text-sm text-destructive" data-testid={`${prefix}-refusal`}>
        {error}
      </p>
    );
  }
  if (loading) {
    return <p className="text-sm text-muted-foreground">{loadingText}</p>;
  }

  const chosen = rows.flatMap((row) => (row.target ? [row.target] : []));
  const plain = plainOf(rows);
  const confirmable = canConfirm(plain);

  const submit = async (): Promise<void> => {
    setSaving(true);
    setRefusal(null);
    try {
      await onConfirm(plain);
    } catch (thrown) {
      /* The host's own sentence, which for the force split names the number AND the night. */
      setRefusal(thrown instanceof Error ? thrown.message : copy.refused);
    } finally {
      setSaving(false);
    }
  };

  if (empty) {
    return (
      <div className="flex flex-wrap items-center gap-2" data-testid={`${prefix}-inline`}>
        <p className="text-sm text-muted-foreground">{empty}</p>
        <Button size="small" onClick={onCancel}>
          {copy.cancel}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1.5" data-testid={`${prefix}-inline`}>
      {/*
        ─────────────────────────────────────────────────────────────────────────────
         ⚠️ [amended UX1.1 §4] ONE ROW PER TARGET, STACKED — NOT ONE WRAPPING LINE.

         This was a single `flex flex-wrap` holding every picker, every count and all three
         buttons, so two units rendered as FOUR controls on one line and read as one wide form
         rather than as two entries. `flex-col` says one entry per line at every width, so
         there is no container query to state and none is added.

         ⚠️ [amended C3c §5b] AND CONFIRM/CANCEL STAY ON THE FIRST ROW — LEFT-ALIGNED. They act on
         the WHOLE set — every host's write takes the full desired state and REPLACES — so
         repeating them per row would read as "confirm this unit", which no host does.
        ─────────────────────────────────────────────────────────────────────────────
      */}
      <div className="flex flex-col gap-2">
        {rows.map((row, index) => (
          <div
            key={row.id}
            /* ⚠️ [UX1.1 §4] THE ROW'S OWN HANDLE. jsdom resolves no cascade, so "one row per
               target" is only assertable if a row is addressable — `flex-col` is invisible to
               it. A data attribute rather than a `data-testid` because a caller means "the row
               for target N", which is an identity, not a name (`data-instance`'s rule, [P6b.2]). */
            {...rowHandle(index)}
            className="flex flex-wrap items-center gap-1.5"
          >
            {renderTarget({
              index,
              value: row.target,
              disabledKeys: new Set(chosen.filter((key) => key !== row.target)),
              onChange: (next) => {
                setRows((current) => current.map((item, at) => (at === index ? { ...item, target: next } : item)));
              },
            })}
            <span className="flex flex-col gap-0.5">
              <InputNumber
                size="small"
                /*
                 * ⚠️ [UX1 §2] THE ROW'S OWN FLOOR, NOT ZERO — the force split's `planDelegation`
                 * refuses a family row dropped below the names already standing on it.
                 *
                 * ⚠️ `max` IS THE HOST'S BOUND AND DELIBERATELY NOT PER-ROW HEADROOM. A per-row
                 * max clamps typing for a reason the operator cannot see; the host's
                 * `canConfirm` refuses the whole set, and the force host's caption names the
                 * night that binds.
                 */
                min={minFor(row.target)}
                max={max}
                className="w-20"
                aria-label={copy.countLabel(plain[index] ?? row, index)}
                data-testid={`${prefix}-count-${String(index)}`}
                value={row.count}
                onChange={(next) => {
                  setRows((current) =>
                    current.map((item, at) => (at === index ? { ...item, count: next ?? 0 } : item)),
                  );
                }}
              />
              {rowCaption?.(plain[index] ?? row, index)}
            </span>

            {/*
              ⚠️ [C3c §5b] NO `ml-auto`, AND `flex-wrap` ON THE ROW — both halves of one fix. The
              pair was pushed to the far edge of a row whose other children are a picker and a
              number, so at the widths operators use it was the first thing to overflow, and an
              OVERFLOWING FLEX ROW CLIPS RATHER THAN SCROLLS. `e2e/specs/fillGaps.spec.ts` is the
              browser claim for the force host; jsdom performs no layout.
            */}
            {index === 0 ? (
              <span className="flex shrink-0 items-center gap-2">
                <Button
                  size="small"
                  type="primary"
                  disabled={!confirmable}
                  loading={saving}
                  data-testid={`${prefix}-confirm`}
                  onClick={() => void submit()}
                >
                  {copy.confirm}
                </Button>
                <Button size="small" data-testid={`${prefix}-cancel`} onClick={onCancel}>
                  {copy.cancel}
                </Button>
              </span>
            ) : null}
          </div>
        ))}
      </div>

      {/* ⚠️ BENEATH THE STACK, because it APPENDS to it — a control that grows a list belongs at
          the end of the list it grows, not beside its first entry. */}
      <div>
        <Button
          size="small"
          type="link"
          disabled={!canAddRow(plain)}
          data-testid={`${prefix}-add-unit`}
          onClick={() => {
            setRows((current) => [...current, blank()]);
          }}
        >
          {copy.addRow}
        </Button>
      </div>

      {footer}

      {refusal ? (
        <p className="text-sm text-destructive" data-testid={`${prefix}-refusal`}>
          {refusal}
        </p>
      ) : null}
    </div>
  );
};
